import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const SWIPE_THRESHOLD = 40; // px mínimos para considerar swipe

const InstagramCarousel = ({ article }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (article?.videoUrl && article?.videoStorageMethod !== 'embed' && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Se o navegador bloquear autoplay, o usuário ainda pode tocar no player.
        });
      }
    }
  }, [article?.videoStorageMethod, article?.videoUrl]);

  if (!article) return null;

  // Helper para extrair ID único da imagem
  const getImageId = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      if (urlObj.pathname.includes('/api/proxy/instagram-image')) {
        const originalUrl = urlObj.searchParams.get('url');
        if (originalUrl) return getImageId(originalUrl);
      }
      const parts = urlObj.pathname.split('/');
      const filename = parts[parts.length - 1];
      const idMatch = filename.match(/(\d+_)+n\.(jpg|png|webp)/);
      return idMatch ? idMatch[0] : urlObj.pathname.toLowerCase();
    } catch {
      return url.toLowerCase();
    }
  };

  const uniqueImages = new Map();
  if (article.image) {
    uniqueImages.set(getImageId(article.image), article.image);
  }
  if (article.allImages && article.allImages.length > 0) {
    article.allImages.forEach(img => {
      const id = getImageId(img);
      if (!uniqueImages.has(id)) uniqueImages.set(id, img);
    });
  }

  const images = Array.from(uniqueImages.values());
  const hasMultipleImages = images.length > 1;
  const videoNeedsInstagram = article.hasVideo && article.videoStorageMethod === 'embed';

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Touch handlers para swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) {
      if (delta > 0 && currentImageIndex < images.length - 1) nextImage();
      else if (delta < 0 && currentImageIndex > 0) prevImage();
    }
    touchStartX.current = null;
  };

  return (
    <Box sx={{ mb: 2, position: 'relative' }}>
      {article.videoUrl && article.videoStorageMethod !== 'embed' ? (
        <Box sx={{ textAlign: 'center' }}>
          <video
            ref={videoRef}
            src={article.videoUrl}
            poster={article.thumbnail || article.image}
            controls
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            style={{
              maxWidth: '100%',
              width: 'auto',
              maxHeight: '600px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        </Box>
      ) : images.length > 0 ? (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}

          >
            <img
              src={images[currentImageIndex]}
              alt={`${article.title} - ${currentImageIndex + 1}/${images.length}`}
              style={{
                maxWidth: '100%',
                width: 'auto',
                maxHeight: '600px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'block',
                userSelect: 'none',
                WebkitUserDrag: 'none',
              }}
            />

            {videoNeedsInstagram && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 10%, rgba(0,0,0,0.68) 100%)',
                  borderRadius: '8px'
                }}
              >
                <Box sx={{ textAlign: 'center', color: '#fff' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 1,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.56)',
                      border: '1px solid rgba(255,255,255,0.7)'
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 36 }} />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Vídeo disponível no Instagram
                  </Typography>
                  <Button
                    component="a"
                    href={article.siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(0,0,0,0.72)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' }
                    }}
                  >
                    Ver no Instagram
                  </Button>
                </Box>
              </Box>
            )}

            {/* Indicador de bolinhas */}
            {hasMultipleImages && (
              <Box sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 0.5,
                zIndex: 2
              }}>
                {images.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: index === currentImageIndex
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(255, 255, 255, 0.4)',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer'
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </Box>
            )}

            {/* Setas — apenas Desktop (md+) */}
            {hasMultipleImages && (
              <>
                {currentImageIndex > 0 && (
                  <IconButton
                    onClick={prevImage}
                    sx={{
                      display: 'flex',
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 32,
                      height: 32,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: '#262626',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
                      '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                )}
                {currentImageIndex < images.length - 1 && (
                  <IconButton
                    onClick={nextImage}
                    sx={{
                      display: 'flex',
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 32,
                      height: 32,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: '#262626',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
                      '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default InstagramCarousel;
